/**
 * Formatting utilities for dates, currency, and status values.
 * Ensures consistent display across the application.
 */

/**
 * Format a date for display with locale support.
 */
export function formatDate(
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {}
): string {
  const d = new Date(date)
  if (isNaN(d.getTime())) {
    return 'Invalid date'
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }

  return d.toLocaleDateString(undefined, defaultOptions)
}

/**
 * Format a date with time.
 */
export function formatDateTime(
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {}
): string {
  const d = new Date(date)
  if (isNaN(d.getTime())) {
    return 'Invalid date'
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  }

  return d.toLocaleString(undefined, defaultOptions)
}

/**
 * Format a relative time (e.g., "2 hours ago", "in 3 days").
 */
export function formatRelativeTime(date: Date | string | number): string {
  const d = new Date(date)
  if (isNaN(d.getTime())) {
    return 'Invalid date'
  }

  const now = new Date()
  const diffMs = d.getTime() - now.getTime()
  const diffSec = Math.round(diffMs / 1000)
  const diffMin = Math.round(diffSec / 60)
  const diffHour = Math.round(diffMin / 60)
  const diffDay = Math.round(diffHour / 24)

  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

  if (Math.abs(diffSec) < 60) {
    return rtf.format(diffSec, 'second')
  } else if (Math.abs(diffMin) < 60) {
    return rtf.format(diffMin, 'minute')
  } else if (Math.abs(diffHour) < 24) {
    return rtf.format(diffHour, 'hour')
  } else {
    return rtf.format(diffDay, 'day')
  }
}

/**
 * Format currency with proper symbol and decimal places.
 */
export function formatCurrency(
  amount: number,
  currency: string = 'CNY'
): string {
  // Convert from smallest unit (cents/fen) to main unit
  const mainAmount = amount / 100

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(mainAmount)
}

/**
 * Status configuration for consistent styling.
 */
export interface StatusConfig {
  label: string
  variant: 'default' | 'success' | 'warning' | 'error' | 'info'
  className: string
}

const statusConfigs: Record<string, StatusConfig> = {
  // Order statuses
  pending: {
    label: 'Pending',
    variant: 'warning',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  },
  paid: {
    label: 'Paid',
    variant: 'success',
    className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  failed: {
    label: 'Failed',
    variant: 'error',
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  refunded: {
    label: 'Refunded',
    variant: 'info',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  },
  cancelled: {
    label: 'Cancelled',
    variant: 'default',
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  },
  // Subscription statuses
  active: {
    label: 'Active',
    variant: 'success',
    className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  past_due: {
    label: 'Past Due',
    variant: 'warning',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  },
  expired: {
    label: 'Expired',
    variant: 'error',
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  // Application statuses
  enabled: {
    label: 'Enabled',
    variant: 'success',
    className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  disabled: {
    label: 'Disabled',
    variant: 'default',
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  },
}

/**
 * Get status configuration for display.
 */
export function getStatusConfig(status: string): StatusConfig {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_')
  if (Object.prototype.hasOwnProperty.call(statusConfigs, normalizedStatus)) {
    return statusConfigs[normalizedStatus]
  }
  return {
    label: status,
    variant: 'default' as const,
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  }
}

/**
 * Format a status for display.
 */
export function formatStatus(status: string): string {
  return getStatusConfig(status).label
}

/**
 * Get the CSS class for a status badge.
 */
export function getStatusClassName(status: string): string {
  return getStatusConfig(status).className
}

/**
 * Format a number with thousands separators.
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat().format(num)
}

/**
 * Format a percentage.
 */
export function formatPercent(
  value: number,
  decimals: number = 1
): string {
  return `${value.toFixed(decimals)}%`
}
